import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { ZOOM } from '../../lib/network/constants';

const clampScale = (k) => Math.min(ZOOM.max, Math.max(ZOOM.min, k));

/**
 * Pan/zoom state for the SVG canvas. `transform` maps world (model) coordinates to screen
 * pixels: screen = world * k + (x, y). `transformRef` always holds the latest value, so pointer
 * handlers can read it without waiting for a render.
 */
export default function useViewport(svgRef) {
  const [transform, setTransform] = useState({ x: 0, y: 0, k: 1 });
  const transformRef = useRef(transform);

  const updateTransform = useCallback((next) => {
    const value = typeof next === 'function' ? next(transformRef.current) : next;
    transformRef.current = value;
    setTransform(value);
  }, []);

  const getRect = useCallback(() => {
    const el = svgRef.current;
    if (!el) {
      return {
        width: 0, height: 0, left: 0, top: 0,
      };
    }
    const rect = el.getBoundingClientRect();
    return {
      width: rect.width, height: rect.height, left: rect.left, top: rect.top,
    };
  }, [svgRef]);

  const toWorld = useCallback((clientX, clientY) => {
    const { left, top } = getRect();
    const t = transformRef.current;
    return { x: (clientX - left - t.x) / t.k, y: (clientY - top - t.y) / t.k };
  }, [getRect]);

  /** Zoom by `factor`, keeping the screen point (sx, sy) fixed. */
  const zoomAt = useCallback((factor, sx, sy) => {
    updateTransform((t) => {
      const k = clampScale(t.k * factor);
      const ratio = k / t.k;
      return { k, x: sx - (sx - t.x) * ratio, y: sy - (sy - t.y) * ratio };
    });
  }, [updateTransform]);

  /** Fit world `bounds` ({ x0, y0, x1, y1 }) into the view; centres the origin when null. */
  const fitBounds = useCallback((bounds, {
    padding = ZOOM.fitPadding,
    maxScale = ZOOM.fitMaxScale,
  } = {}) => {
    const { width, height } = getRect();
    if (width === 0 || height === 0) return;
    if (!bounds) {
      updateTransform({ k: 1, x: width / 2, y: height / 2 });
      return;
    }
    const bw = Math.max(bounds.x1 - bounds.x0, 1);
    const bh = Math.max(bounds.y1 - bounds.y0, 1);
    const k = clampScale(Math.min((width - 2 * padding) / bw, (height - 2 * padding) / bh, maxScale));
    const cx = (bounds.x0 + bounds.x1) / 2;
    const cy = (bounds.y0 + bounds.y1) / 2;
    updateTransform({ k, x: width / 2 - cx * k, y: height / 2 - cy * k });
  }, [getRect, updateTransform]);

  // Wheel zoom needs a non-passive listener (React registers wheel as passive).
  useEffect(() => {
    const el = svgRef.current;
    if (!el) return undefined;
    const onWheel = (event) => {
      event.preventDefault();
      const rect = el.getBoundingClientRect();
      const delta = event.deltaMode === 1 ? event.deltaY * 16 : event.deltaY;
      const factor = Math.exp(-delta * ZOOM.wheelSpeed * (event.ctrlKey ? 4 : 1));
      zoomAt(factor, event.clientX - rect.left, event.clientY - rect.top);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [svgRef, zoomAt]);

  return {
    transform,
    transformRef,
    updateTransform,
    toWorld,
    zoomAt,
    fitBounds,
    getRect,
    clampScale,
  };
}
